import { Injectable } from '@angular/core'
import { BehaviorSubject, catchError, EMPTY, Observable, Subscription, tap, timer } from 'rxjs'
import { LogService } from 'htv-lib'
import {
  Bill,
  HTVLocation,
  LocationOccupant,
  OccupancyStatus,
  UpdateLocationDto,
  WakeUpAlarmConfig,
} from '../interfaces/location'
import { environment } from '../environments/environment'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { StorageService } from './storage.service'
import { HttpUtil } from '../utils/http-util'
import { StateDataService } from './state-data.service'

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  readonly CMN_LOCATION_API_URL = environment.cmnLocationApiUrl
  timerSubscription?: Subscription

  location?: HTVLocation
  location$ = new BehaviorSubject<HTVLocation | undefined>(undefined)

  readonly refreshTimerDelayInMs = 10000 // TODO

  // STATE
  occupancyStatus!: OccupancyStatus
  occupancyStatus$!: BehaviorSubject<OccupancyStatus>
  private readonly occupancyStatusKey = 'LocationService-occupancyStatus'

  presenceStatus!: boolean
  presenceStatus$!: BehaviorSubject<boolean>
  private readonly presenceStatusKey = 'LocationService-presenceStatus'

  constructor(
    private httpClient: HttpClient,
    private logService: LogService,
    private stateDataService: StateDataService,
    private storageService: StorageService
  ) {
    // do nothing
  }

  init(): void {
    this.logService.info('[LocationService] Init')

    this.occupancyStatus = this.getLocalOccupancyStatus()
    this.occupancyStatus$ = new BehaviorSubject<OccupancyStatus>(this.occupancyStatus)

    this.presenceStatus = this.getLocalPresenceStatus()
    this.presenceStatus$ = new BehaviorSubject<boolean>(this.presenceStatus)
  }

  initFromProject(location?: HTVLocation): void {
    this.logService.debug('[LocationService] Initializing with Humecloud data')

    this.handleLocation(location)
    this.registerRefreshTimer()
  }

  private registerRefreshTimer(): void {
    if (this.timerSubscription) {
      this.timerSubscription?.unsubscribe()
    }
    this.timerSubscription = timer(0, this.refreshTimerDelayInMs).subscribe(() => {
      if (!this.location?.id) {
        this.logService.debug(`[LocationService] There is no location attached`)
        return
      }

      this.logService.debug(`[LocationService] Refreshing location data...`)
      this.getRemoteLocation(this.location.id).subscribe(location => {
        this.handleLocation(location)
      })
    })
  }

  private handleLocation(location?: HTVLocation): void {
    this.handleOccupancyStatusChange(location?.occupancyStatus)
    this.handlePresenceStatusChange(location?.presenceStatus)

    this.location = location
    this.location$.next(this.location)
  }

  private handleOccupancyStatusChange(occupancyStatus?: OccupancyStatus): void {
    if (!occupancyStatus) {
      return
    }

    if (this.occupancyStatus !== occupancyStatus) {
      this.logService.debug(`[LocationService] occupancyStatus: ${this.occupancyStatus} -> ${occupancyStatus}`)
      this.occupancyStatus = occupancyStatus
      this.occupancyStatus$.next(this.occupancyStatus)
      this.storageService.saveString(this.occupancyStatusKey, OccupancyStatus[occupancyStatus])
    }
  }

  private handlePresenceStatusChange(presenceStatus?: boolean): void {
    if (!presenceStatus) {
      return
    }

    if (this.presenceStatus !== presenceStatus) {
      this.logService.debug(`[LocationService] presenceStatus: ${this.presenceStatus} -> ${presenceStatus}`)
      this.presenceStatus = presenceStatus
      this.presenceStatus$.next(this.presenceStatus)
      this.storageService.saveBoolean(this.presenceStatusKey, this.presenceStatus)
    }
  }

  getLocationOccupantLastName(): string | undefined {
    const occupants = this.location?.occupants
    const occupant = occupants ? (occupants[0] as LocationOccupant) : undefined
    return occupant ? occupant.lastName : undefined
  }

  getLocationOccupantSalutation(): string | undefined {
    const occupants = this.location?.occupants
    const occupant = occupants ? (occupants[0] as LocationOccupant) : undefined
    const title = occupant?.salutation ? occupant.salutation : '‎'
    return title
  }

  getLocationOccupantFirstName(): string | undefined {
    const occupants = this.location?.occupants
    const occupant = occupants ? (occupants[0] as LocationOccupant) : undefined
    return occupant ? occupant.firstName : undefined
  }

  updateWakeUpAlarmConfigs(configs: WakeUpAlarmConfig[]): Observable<HTVLocation> {
    if (!this.location) {
      return EMPTY
    }

    const location: HTVLocation = {
      ...this.location,
      wakeupAlarmConfigs: configs,
    }
    return this.updateLocation(location)
  }

  // Backward compatibility for basic widget (single alarm)
  updateWakeUpAlarmConfig(config?: WakeUpAlarmConfig): Observable<HTVLocation> {
    const configs = config ? [config] : []
    return this.updateWakeUpAlarmConfigs(configs)
  }

  getRemoteLocation(locationId: string): Observable<HTVLocation> {
    const url = `${this.CMN_LOCATION_API_URL}/public/locations/${locationId}`
    const user = this.stateDataService.deviceId ?? ''
    const headers = new HttpHeaders().set('htv-auth', HttpUtil.getHTVBasicAuth(user))
    return this.httpClient.get<HTVLocation>(url, { headers }).pipe(
      catchError(err => {
        this.logService.error(`Could not retrieve location ${locationId}`)
        this.logService.debug(err.message)
        return EMPTY
      })
    )
  }

  updateLocation(location: HTVLocation): Observable<HTVLocation> {
    if (!this.location?.id) {
      return EMPTY
    }

    const url = `${this.CMN_LOCATION_API_URL}/public/locations/${this.location.id}`
    const user = this.stateDataService.deviceId ?? ''
    const headers = new HttpHeaders().set('htv-auth', HttpUtil.getHTVBasicAuth(user))

    const updateLocationDto: UpdateLocationDto = {
      id: this.location.id,
      type: this.location.type,
      wakeupAlarmConfigs: location.wakeupAlarmConfigs,
    }

    return this.httpClient.put<HTVLocation>(url, updateLocationDto, { headers }).pipe(
      tap(location => {
        this.handleLocation(location)
      }),
      catchError(err => {
        this.logService.error(`Could not update location`)
        this.logService.debug(err.message)
        throw err
      })
    )
  }

  getClientBill(locationId: string): Observable<Bill> {
    const url = `${this.CMN_LOCATION_API_URL}/public/locations/${locationId}/client-bill`
    const user = this.stateDataService.deviceId ?? ''
    const headers = new HttpHeaders().set('htv-auth', HttpUtil.getHTVBasicAuth(user))
    return this.httpClient.get<Bill>(url, { headers }).pipe(
      catchError(err => {
        this.logService.error(`Could not retrieve client bill for location ${locationId}`)
        this.logService.debug(err.message)
        return EMPTY
      })
    )
  }

  getLocalOccupancyStatus(): OccupancyStatus {
    try {
      const str = this.storageService.getString(this.occupancyStatusKey)
      if (str) {
        return OccupancyStatus[str as keyof typeof OccupancyStatus]
      } else {
        return OccupancyStatus.UNKNOWN
      }
    } catch (error) {
      return OccupancyStatus.UNKNOWN
    }
  }

  getLocalPresenceStatus(): boolean {
    try {
      return this.storageService.getBoolean(this.presenceStatusKey) ?? false
    } catch (error) {
      return false
    }
  }
}
